import { useMemo, useRef, useEffect } from 'react';
import { marked } from 'marked';

interface StyledMarkdownProps {
  content: string;
  className?: string;
  blockPrefix?: string;
  onParagraphClick?: (blockId: string, text: string) => void;
}

function transformCallouts(html: string): string {
  // Convert blockquotes that start with a bold label like **Note:**, **Warning:**, **Tip:**, etc.
  return html.replace(
    /<blockquote>\s*<p>\s*<strong>([^:]+):?<\/strong>\s*(.*?)<\/p>(.*?)<\/blockquote>/gis,
    (_match, label, firstParagraph, rest) => {
      const normalized = label.toLowerCase().trim();
      let variant = 'info';
      if (normalized.includes('warn') || normalized.includes('caution') || normalized.includes('important')) {
        variant = 'warning';
      } else if (normalized.includes('tip') || normalized.includes('success') || normalized.includes('done')) {
        variant = 'success';
      }
      const body = `<p>${firstParagraph}</p>${rest}`;
      return `<div class="callout callout-${variant}" data-callout="${variant}">${body}</div>`;
    }
  );
}

function injectBlockIds(html: string, prefix: string): string {
  let index = 0;
  return html.replace(
    /<(p|li|h[1-6]|blockquote|pre|table|div class="callout[^"]*")([^>]*)>/g,
    (match, tag, attrs) => {
      const id = `${prefix}-block-${index++}`;
      if (attrs.includes('id=')) {
        return match.replace(/id="[^"]*"/, `id="${id}"`).replace(/>$/, ` data-block-id="${id}">`);
      }
      return `<${tag}${attrs} id="${id}" data-block-id="${id}">`;
    }
  );
}

export default function StyledMarkdown({ content, className = '', blockPrefix = 'md', onParagraphClick }: StyledMarkdownProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  const html = useMemo(() => {
    const raw = marked.parse(content, { async: false, gfm: true }) as string;
    return injectBlockIds(transformCallouts(raw), blockPrefix);
  }, [content, blockPrefix]);

  useEffect(() => {
    if (!onParagraphClick || !containerRef.current) return;

    const container = containerRef.current;

    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const block = target.closest('[data-block-id]') as HTMLElement | null;
      if (!block || !container.contains(block)) return;
      const blockId = block.getAttribute('data-block-id');
      if (!blockId) return;
      const text = block.textContent || '';
      onParagraphClick(blockId, text.trim());
    };

    container.addEventListener('click', handleClick);
    return () => container.removeEventListener('click', handleClick);
  }, [onParagraphClick, html]);

  return (
    <div
      ref={containerRef}
      className={`prose max-w-none ${onParagraphClick ? 'interactive-blocks' : ''} ${className}`}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
